import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  ArrowLeft,
  Download,
  FileJson,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Eye,
  EyeOff,
  Image,
  File,
} from "lucide-react";
import { cocbGetWorkOrder, cocbDeleteWorkOrder, cocbGetWorkOrderFileUrl, cocbGetWorkOrderPreviewUrl } from "../api";
import "./COCBWorkOrderDetail.css";

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="cocb-collapsible-section">
      <button className="cocb-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="cocb-section-content">{children}</div>}
    </div>
  );
};

// Recursive JSON Renderer
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="cocb-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="cocb-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="cocb-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="cocb-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="cocb-json-empty">[]</span>;
    return (
      <div className="cocb-json-array">
        {data.map((item, index) => (
          <div key={index} className="cocb-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="cocb-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="cocb-json-empty">{"{}"}</span>;
    return (
      <div className="cocb-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="cocb-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="cocb-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

const COCBWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("extracted");
  const [copiedField, setCopiedField] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(true);
  const [fileFullscreen, setFileFullscreen] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(false);

  const fileUrl = workOrderId ? cocbGetWorkOrderFileUrl(workOrderId) : null;
  const previewUrl = workOrderId ? cocbGetWorkOrderPreviewUrl(workOrderId) : null;

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
    }
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await cocbGetWorkOrder(workOrderId);
      setWorkOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this work order? This cannot be undone.")) {
      return;
    }

    try {
      await cocbDeleteWorkOrder(workOrderId);
      navigate("/v1/cocb/history");
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleDownloadJSON = () => {
    if (!workOrder?.extracted_data) return;

    const data = workOrder.extracted_data;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!workOrder?.extracted_data) return;

    const data = workOrder.extracted_data;
    const flatData = {};

    const flatten = (obj, prefix = "") => {
      for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          flatten(value, newKey);
        } else {
          flatData[newKey] = value;
        }
      }
    };
    flatten(data);

    const headers = Object.keys(flatData);
    const values = headers.map((h) => {
      const val = flatData[h];
      const str = String(val === undefined || val === null ? "" : val).replace(/"/g, '""');
      return `"${str}"`;
    });
    const csv = [headers.join(","), values.join(",")].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={20} className="status-icon completed" />;
      case "failed":
        return <XCircle size={20} className="status-icon failed" />;
      case "processing":
        return <RefreshCw size={20} className="status-icon processing spin" />;
      default:
        return <AlertCircle size={20} className="status-icon pending" />;
    }
  };

  const getFileExtension = (filename) => {
    if (!filename) return 'pdf';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'pdf';
  };

  const isImageFile = (filename) => {
    const ext = getFileExtension(filename);
    return ['tiff', 'tif', 'jpg', 'jpeg', 'png', 'gif'].includes(ext);
  };

  if (loading) {
    return (
      <div className="cocb-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error && !workOrder) {
    return (
      <div className="cocb-detail-error">
        <XCircle size={48} />
        <h3>Error Loading Work Order</h3>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="cocb-detail-error">
        <AlertCircle size={48} />
        <h3>Work Order Not Found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="cocb-work-order-detail">
      {/* Back Button */}
      <button className="cocb-back-button" onClick={() => navigate("/v1/cocb/history")}>
        <ArrowLeft size={18} />
        Back to Work Orders
      </button>

      {/* Header */}
      <div className="cocb-detail-header">
        <div className="cocb-detail-header-left">
          <div className="cocb-detail-title">
            <h1>
              <FileText size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span className="cocb-pipeline-tag">
              COCB
            </span>
          </div>
          <div className="cocb-detail-meta">
            <span className="cocb-meta-item">
              <strong>ID:</strong> {workOrder._id}
              <button
                className="cocb-copy-btn"
                onClick={() => copyToClipboard(workOrder._id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.uid && (
              <span className="cocb-meta-item">
                <strong>UID:</strong> {workOrder.uid}
              </span>
            )}
          </div>
        </div>

        <div className="cocb-detail-header-right">
          <div className={`cocb-status-badge status-${workOrder.status}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {error && (
        <div className="cocb-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Info Cards */}
      <div className="cocb-info-cards">
        <div className="cocb-info-card">
          <Clock size={20} />
          <div>
            <span className="cocb-info-label">Created</span>
            <span className="cocb-info-value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
        <div className="cocb-info-card">
          <FileText size={20} />
          <div>
            <span className="cocb-info-label">Pages</span>
            <span className="cocb-info-value">{workOrder.total_pages || "-"}</span>
          </div>
        </div>
        <div className="cocb-info-card">
          <RefreshCw size={20} />
          <div>
            <span className="cocb-info-label">Processing Time</span>
            <span className="cocb-info-value">
              {workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(2)}s` : "-"}
            </span>
          </div>
        </div>
        <div className="cocb-info-card">
          <FileText size={20} />
          <div>
            <span className="cocb-info-label">Model</span>
            <span className="cocb-info-value">{workOrder.model || "Qwen VL"}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="cocb-action-bar">
        <button 
          className={`cocb-action-btn toggle ${showFileViewer ? 'active' : ''}`}
          onClick={() => setShowFileViewer(!showFileViewer)}
          title={showFileViewer ? "Hide File Viewer" : "Show File Viewer"}
        >
          {showFileViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          {showFileViewer ? "Hide File" : "Show File"}
        </button>
        <button className="cocb-action-btn primary" onClick={handleDownloadJSON}>
          <FileJson size={16} />
          JSON
        </button>
        <button className="cocb-action-btn secondary" onClick={handleDownloadCSV}>
          <FileSpreadsheet size={16} />
          CSV
        </button>
        <button className="cocb-action-btn refresh" onClick={fetchWorkOrder}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="cocb-action-btn danger" onClick={handleDelete}>
          <Trash2 size={16} />
          Delete
        </button>
      </div>

      {/* Split View Container */}
      <div className={`cocb-split-container ${showFileViewer ? 'with-file' : 'no-file'} ${fileFullscreen ? 'file-fullscreen' : ''}`}>
        {/* File Viewer Panel */}
        {showFileViewer && (
          <div className="cocb-file-panel">
            <div className="cocb-file-header">
              <h3>
                {isImageFile(workOrder.filename) ? <Image size={18} /> : <FileText size={18} />}
                {workOrder.filename || 'Document'}
              </h3>
              <div className="cocb-file-controls">
                <span className="cocb-file-type-badge">
                  {getFileExtension(workOrder.filename).toUpperCase()}
                </span>
                <button 
                  className="cocb-file-control-btn"
                  onClick={() => setFileFullscreen(!fileFullscreen)}
                  title={fileFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {fileFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="cocb-file-control-btn"
                  title="Download Original"
                >
                  <Download size={16} />
                </a>
              </div>
            </div>
            <div className="cocb-file-viewer">
              {workOrderId && !fileLoadError ? (
                isImageFile(workOrder.filename) ? (
                  <div className="cocb-image-viewer-scroll">
                    {workOrder.total_pages > 1 && (
                      <div className="cocb-page-count-badge">
                        {workOrder.total_pages} pages
                      </div>
                    )}
                    <img
                      src={previewUrl}
                      alt={workOrder.filename || 'Document'}
                      className="cocb-document-image-scroll"
                      onError={() => setFileLoadError(true)}
                    />
                  </div>
                ) : (
                  <iframe
                    src={previewUrl}
                    title="Document Viewer"
                    className="cocb-file-iframe"
                    onError={() => setFileLoadError(true)}
                  />
                )
              ) : (
                <div className="cocb-file-unavailable">
                  <File size={48} />
                  <p>{fileLoadError ? "Unable to display this file" : "File not available"}</p>
                  <a href={fileUrl} download className="cocb-download-link">
                    <Download size={16} /> Download Original File
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="cocb-data-panel">
          {/* Tabs */}
          <div className="cocb-tabs">
            <button
              className={`cocb-tab ${activeTab === "extracted" ? "active" : ""}`}
              onClick={() => setActiveTab("extracted")}
            >
              Extracted Data
            </button>
            <button
              className={`cocb-tab ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              Raw JSON
            </button>
            <button
              className={`cocb-tab ${activeTab === "signatures" ? "active" : ""}`}
              onClick={() => setActiveTab("signatures")}
            >
              Signatures
            </button>
            <button
              className={`cocb-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              Metadata
            </button>
          </div>

          {/* Tab Content */}
          <div className="cocb-tab-content">
            {activeTab === "extracted" && (
              <div className="cocb-extracted-data">
                {workOrder.extracted_data ? (
                  Object.entries(workOrder.extracted_data).map(([sectionKey, sectionData]) => (
                    <CollapsibleSection key={sectionKey} title={sectionKey}>
                      {typeof sectionData === "object" && sectionData !== null ? (
                        <div className="cocb-fields-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Value</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(sectionData).map(([fieldKey, fieldValue]) => (
                                <tr key={fieldKey}>
                                  <td className="field-name">{fieldKey}</td>
                                  <td className="field-value">
                                    {typeof fieldValue === "object"
                                      ? JSON.stringify(fieldValue)
                                      : String(fieldValue ?? "")}
                                  </td>
                                  <td className="field-actions">
                                    <button
                                      className="cocb-copy-btn"
                                      onClick={() =>
                                        copyToClipboard(
                                          typeof fieldValue === "object"
                                            ? JSON.stringify(fieldValue)
                                            : String(fieldValue ?? ""),
                                          `${sectionKey}.${fieldKey}`
                                        )
                                      }
                                    >
                                      <Copy size={14} />
                                      {copiedField === `${sectionKey}.${fieldKey}` && (
                                        <span className="copied-tooltip">Copied!</span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="cocb-simple-value">{String(sectionData)}</div>
                      )}
                    </CollapsibleSection>
                  ))
                ) : (
                  <div className="cocb-no-data">
                    <AlertCircle size={48} />
                    <p>No extracted data available</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "raw" && (
              <div className="cocb-raw-json">
                <div className="cocb-raw-json-header">
                  <button onClick={handleDownloadJSON}>
                    <Download size={16} /> Download
                  </button>
                  <button
                    onClick={() =>
                      copyToClipboard(JSON.stringify(workOrder.extracted_data, null, 2), "raw")
                    }
                  >
                    <Copy size={16} /> Copy
                    {copiedField === "raw" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="cocb-json-viewer">
                  {workOrder.extracted_data ? (
                    <JsonRenderer data={workOrder.extracted_data} />
                  ) : (
                    "No data available"
                  )}
                </pre>
              </div>
            )}

            {activeTab === "signatures" && (
              <div className="cocb-signatures-tab">
                {(() => {
                  const signatureImages = workOrder.extracted_data?.signature_images || {};
                  const signatureKeys = Object.keys(signatureImages).filter(key => {
                    const value = signatureImages[key];
                    return value && value.trim() !== '';
                  });
                  
                  if (signatureKeys.length === 0) {
                    return (
                      <div className="cocb-no-signatures">
                        <AlertCircle size={48} />
                        <p>No signatures detected in this document</p>
                        <span className="cocb-signatures-hint">
                          Signatures are automatically extracted using AWS Textract when processing new documents.
                        </span>
                      </div>
                    );
                  }
                  
                  // Format signature label for display
                  const formatLabel = (key) => {
                    return key
                      .replace(/_/g, ' ')
                      .replace(/P(\d+)$/, '(Page $1)')
                      .replace(/(\d+)$/, ' $1');
                  };
                  
                  return (
                    <div className="cocb-signatures-grid">
                      {signatureKeys.map(key => {
                        const base64Image = signatureImages[key];
                        return (
                          <div key={key} className="cocb-signature-card">
                            <div className="cocb-signature-label">{formatLabel(key)}</div>
                            <div className="cocb-signature-image-container">
                              <img src={base64Image} alt={formatLabel(key)} className="cocb-signature-image" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === "metadata" && (
              <div className="cocb-metadata">
                <table className="cocb-metadata-table">
                  <tbody>
                    <tr>
                      <td>Work Order ID</td>
                      <td>{workOrder._id}</td>
                    </tr>
                    <tr>
                      <td>Filename</td>
                      <td>{workOrder.filename || "-"}</td>
                    </tr>
                    <tr>
                      <td>UID</td>
                      <td>{workOrder.uid || "-"}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>{workOrder.status}</td>
                    </tr>
                    <tr>
                      <td>Total Pages</td>
                      <td>{workOrder.total_pages || "-"}</td>
                    </tr>
                    <tr>
                      <td>Processing Time</td>
                      <td>{workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(3)}s` : "-"}</td>
                    </tr>
                    <tr>
                      <td>Model</td>
                      <td>{workOrder.model || "-"}</td>
                    </tr>
                    <tr>
                      <td>Created At</td>
                      <td>{formatDate(workOrder.created_at)}</td>
                    </tr>
                    <tr>
                      <td>Updated At</td>
                      <td>{formatDate(workOrder.updated_at)}</td>
                    </tr>
                    {workOrder.api_log && (
                      <>
                        <tr className="cocb-metadata-section">
                          <td colSpan={2}>API Request Details</td>
                        </tr>
                        <tr>
                          <td>Method</td>
                          <td>{workOrder.api_log.method || "-"}</td>
                        </tr>
                        <tr>
                          <td>Endpoint</td>
                          <td>{workOrder.api_log.endpoint || "-"}</td>
                        </tr>
                        <tr>
                          <td>IP</td>
                          <td>{workOrder.api_log.ip_address || "-"}</td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>
                            {workOrder.api_log.status || "-"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Request payload</td>
                          <td>
                            <pre className="cocb-metadata-payload">
                              {workOrder.api_log.request_params &&
                              Object.keys(workOrder.api_log.request_params).length > 0
                                ? JSON.stringify(workOrder.api_log.request_params, null, 2)
                                : "-"}
                            </pre>
                          </td>
                        </tr>
                        <tr>
                          <td>Response Time</td>
                          <td>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Created By</td>
                          <td>{workOrder.api_log.created_by || "-"}</td>
                        </tr>
                        <tr>
                          <td>Timestamp</td>
                          <td>{formatDate(workOrder.api_log.timestamp)}</td>
                        </tr>
                      </>
                    )}
                    {workOrder.error && (
                      <tr className="error-row">
                        <td>Error</td>
                        <td>{workOrder.error}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default COCBWorkOrderDetail;
